import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import * as configStripState from '@epicuro-next/state/config-strip-state';
import { selectProductLoading } from '@epicuro-next/state/config-strip-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { OrderType, PreviewItem } from 'graphql-api/gql-api-interfaces';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

interface SizeQuantityById {
  [productId: string]: Omit<
    configStripState.DeliveryItem,
    'useInventoryDate'
  > & {
    productId: string;
  };
}

@Component({
  selector: 'epc-preview-product-sizes',
  templateUrl: './preview-product-sizes.component.html',
  styleUrls: ['./preview-product-sizes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PreviewProductSizesComponent implements OnInit, OnDestroy {
  @Input() public articleId: string;

  @Input() public brandId: string;

  @Input() set previewItems(previewItems: PreviewItem[]) {
    this.previewItems$.next(previewItems);
  }

  public previewItems$ = new ReplaySubject<PreviewItem[]>();

  private destroy$ = new Subject<void>();

  constructor(private store: Store) {}

  public isProductLoaded$ = this.store.pipe(
    select(configStripState.selectProduct),
    filter(isNonNullish),
  );

  public selectCached$: Observable<configStripState.MemoProductSizes | null> =
    this.store.pipe(
      select(configStripState.selectMemoProductSizes, () => ({
        articleId: this.articleId,
        brandId: this.brandId,
      })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private sizesPrepacks$: Observable<SizeQuantityById> =
    this.selectCached$.pipe(
      filter(isNonNullish),
      withLatestFrom(this.previewItems$),
      map(([{ prepacks }, previewItems]) => {
        const [anyPreviewItem] = previewItems;
        const quantities =
          prepacks?.find(({ productId }) => productId === anyPreviewItem.itemId)
            ?.quantities || [];

        return previewItems.reduce((all: SizeQuantityById, previewItem) => {
          const reduced = quantities?.reduce(
            (acc: SizeQuantityById, item) => ({
              ...acc,
              [item.productId]: {
                quantity:
                  item.quantity *
                    (previewItem.confirmedQuantity ??
                      previewItem.requestedQuantity) +
                  (all[item.productId]?.quantity ?? 0),
                usedTrueDemandQuantity: 0,
                productId: item.productId,
              },
            }),
            {},
          );
          return reduced;
        }, {} as SizeQuantityById);
      }),
    );

  private sizesMaterials$: Observable<SizeQuantityById> =
    this.previewItems$.pipe(
      map((previewItems) =>
        previewItems.reduce(
          (acc, previewItem) => ({
            ...acc,
            [previewItem.itemId]: {
              quantity:
                (acc[previewItem.itemId]?.quantity || 0) +
                (previewItem.confirmedQuantity ??
                  previewItem.requestedQuantity),
              usedTrueDemandQuantity: 0,
              productId: previewItem.itemId,
            },
          }),
          {} as SizeQuantityById,
        ),
      ),
    );

  public materialsWithSizes$: Observable<
    configStripState.ProductExtendedVariant[]
  > = this.selectCached$.pipe(
    filter(isNonNullish),
    withLatestFrom(this.sizesMaterials$, this.sizesPrepacks$),
    map(([{ materials }, sizesMaterials, sizesPrepacks]) =>
      this.getMaterialsWithSizes(materials, sizesMaterials, sizesPrepacks),
    ),
  );

  public sizesMerged$: Observable<configStripState.DeliveryItems> =
    combineLatest([this.sizesMaterials$, this.sizesPrepacks$]).pipe(
      map(([materialSizes, prepackSizes]) => ({
        orderType: OrderType.Now,
        items: { ...materialSizes, ...prepackSizes },
      })),
    );

  public differentMaterialDimensions$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.DifferentMaterialDimensions
        ],
      ),
    ),
  );

  public productLoading$ = this.store.select(selectProductLoading);

  public ngOnInit() {
    this.store
      .pipe(
        select(configStripState.selectMemoProductSizes, () => ({
          articleId: this.articleId,
          brandId: this.brandId,
        })),
        take(1),
        shareReplay({ bufferSize: 1, refCount: true }),
        filter((cache) => !cache),
      )
      .subscribe(() => {
        this.triggerDispatch();
      });
  }

  private getMaterialsWithSizes(
    materials: configStripState.ProductExtendedVariant[],
    sizesMaterials: SizeQuantityById,
    sizesPrepacks: SizeQuantityById,
  ) {
    return materials.filter(({ children }) =>
      children?.find(({ productId }) =>
        Object.values({ ...sizesMaterials, ...sizesPrepacks })
          .map(({ productId: sizeProductId }) => sizeProductId)
          .includes(productId),
      ),
    );
  }

  private triggerDispatch() {
    this.store.dispatch(
      configStripState.loadProduct({
        productId: this.articleId,
        brandId: this.brandId,
      }),
    );

    this.isProductLoaded$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        takeUntil(this.store.select(configStripState.selectIsConfigStripOpen)),
      )
      .subscribe(() => {
        this.store.dispatch(configStripState.selectNowDeliveryPeriod());
      });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
